import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Reusable color-coded "Jump to on this page" navigation pill bar.
 * Used on marketing pages to signal there's more content below the fold
 * and let users teleport to the section that matters to them.
 *
 * Usage:
 *   <JumpToNav
 *     items={[
 *       { icon: Smile, label: 'Why patients love it', href: '#why', color: 'rose' },
 *       { icon: Lock, label: 'Privacy', href: '#privacy', color: 'slate' },
 *       { icon: Stethoscope, label: 'Try a sample doctor', to: '/dr/dr-rajesh-sharma', color: 'amber' },
 *     ]}
 *     testId="patients-jump-nav"
 *   />
 */
const COLOR_MAP = {
  rose: 'bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-600 hover:border-rose-600',
  sky: 'bg-sky-50 border-sky-200 text-sky-800 hover:bg-sky-600 hover:border-sky-600',
  slate: 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-900 hover:border-slate-900',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-600 hover:border-emerald-600',
  amber: 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-500 hover:border-amber-500',
  violet: 'bg-violet-50 border-violet-200 text-violet-800 hover:bg-violet-600 hover:border-violet-600',
  teal: 'bg-teal-50 border-teal-200 text-teal-800 hover:bg-teal-600 hover:border-teal-600',
  fuchsia: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-600 hover:border-fuchsia-600',
};

export default function JumpToNav({ items = [], testId = 'jump-nav', className = '' }) {
  if (!items.length) return null;
  return (
    <nav
      className={`mt-6 ${className}`}
      aria-label="Page section shortcuts"
      data-testid={testId}
    >
      <p className="font-bold text-slate-500 uppercase tracking-[0.18em] mb-2.5 flex items-center gap-1.5" style={{ fontSize: 'clamp(0.65rem, 0.7vw, 0.8rem)' }}>
        <ArrowRight size={11} /> Jump to on this page
      </p>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => {
          const Icon = it.icon;
          const colorClasses = COLOR_MAP[it.color] || COLOR_MAP.sky;
          const baseClasses = `inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border-2 font-semibold hover:text-white transition-colors ${colorClasses}`;
          const itemTestId = `${testId}-item-${i}`;
          const styleObj = { fontSize: 'clamp(0.75rem, 0.85vw, 0.95rem)' };

          if (it.to) {
            return (
              <Link
                key={i}
                to={it.to}
                className={baseClasses}
                style={styleObj}
                data-testid={itemTestId}
              >
                {Icon && <Icon size={14} />} {it.label}
              </Link>
            );
          }

          return (
            <a
              key={i}
              href={it.href}
              className={baseClasses}
              style={styleObj}
              data-testid={itemTestId}
            >
              {Icon && <Icon size={14} />} {it.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
